import ReactECharts from 'echarts-for-react'
import { CalendarDays } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useApp } from '../../context/AppContext'
import type { Answer, TimelinePoint } from '../../lib/types'
import { ConfidenceBadge, DataTag } from '../Domain'
import { Badge, Card, CardContent, CardHeader, CardTitle, NadiaLoader, Skeleton } from '../ui'

/**
 * Serie mensual de WHEN.
 *
 * ECharts entra por `echarts-for-react` en vez de a pelo porque el componente
 * ya resuelve el ciclo de vida del `dispose()`, que si se olvida deja el
 * canvas colgado al cambiar de tema.
 *
 * El eje X usa el periodo `YYYY-MM` tal cual. Los nombres de mes los pone el
 * servidor en el titular, no aqui: si se compusieran en el cliente habria dos
 * tablas de meses y volveria el bug del locale del proceso.
 */
export function DemandChart({ timeline, height = 260 }: { timeline: TimelinePoint[]; height?: number }) {
  const { theme } = useApp()
  const options = useMemo(() => {
    const dark = theme !== 'light'
    const text = dark ? '#cbd5e1' : '#334155'
    const grid = dark ? 'rgba(148,163,184,0.18)' : 'rgba(51,65,85,0.15)'
    return {
      backgroundColor: 'transparent',
      grid: { left: 42, right: 24, top: 22, bottom: 34 },
      tooltip: { trigger: 'axis' as const },
      xAxis: {
        type: 'category' as const,
        data: timeline.map((p) => p.period),
        axisLabel: { color: text, fontSize: 10, rotate: 45 },
        axisLine: { lineStyle: { color: grid } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        min: 0,
        max: 1,
        axisLabel: { color: text, fontSize: 10, formatter: (v: number) => v.toFixed(1) },
        splitLine: { lineStyle: { color: grid } },
      },
      series: [
        {
          type: 'bar' as const,
          data: timeline.map((p) => p.value),
          itemStyle: { borderRadius: [4, 4, 0, 0] },
          markLine: {
            symbol: 'none',
            lineStyle: { color: grid, type: 'dashed' as const },
            // `insideEndTop`: el valor viaja con la animacion, pero al terminar
            // el barrido queda DENTRO de la grafica (position 'end' lo cortaba
            // por el borde derecho).
            label: {
              show: true,
              position: 'insideEndTop',
              color: text,
              fontSize: 10,
              fontWeight: 600,
            },
            data: [{ type: 'average' as const, name: 'avg' }],
          },
        },
      ],
    }
  }, [timeline, theme])

  if (!timeline.length) return null
  return <ReactECharts option={options} style={{ height }} notMerge lazyUpdate />
}

export function WhenPanel({ answer, loading }: { answer?: Answer; loading: boolean }) {
  const { t } = useTranslation()
  const { locale } = useApp()

  if (loading) {
    return (
      <div className="space-y-4">
        <NadiaLoader
          minHeight="min-h-[260px]"
          message={t('loading.loadingDemand', 'Calculando curva de demanda y estacionalidad...')}
          subtitle={t('loading.nadiaProcessing', 'Consultando modelos analíticos de Laponia...')}
        />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </div>
    )
  }
  if (!answer) return null

  const top = answer.windows[0]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {answer.headline[locale] ?? answer.headline.en}
        </h1>
        <p className="text-muted-foreground mt-1 max-w-2xl text-sm">{t('question.when.help')}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <ConfidenceBadge level={answer.confidence} score={answer.confidence_score} showScore />
          {answer.signal_weak ? <Badge variant="warning">{t('common.estimated')}</Badge> : null}
          {answer.freshness.status === 'stale' ? (
            <Badge variant="danger">{t('common.stale')}</Badge>
          ) : null}
        </div>
      </div>

      {answer.timeline.length ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('when.timeline')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DemandChart timeline={answer.timeline} />
          </CardContent>
        </Card>
      ) : null}

      {top ? (
        <Card className="border-primary/40 bg-primary/5">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle>{t('when.best')}</CardTitle>
              <Badge variant="secondary">
                {t(`season.${top.season}`) ?? top.season}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-medium">
              {top.rationale[locale] ?? top.rationale.en}
            </p>
            <div className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
              <div>
                <p className="text-muted-foreground text-xs">{t('when.windowStart')}</p>
                <p className="font-medium">{top.start}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t('when.windowEnd')}</p>
                <p className="font-medium">{top.end}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t('when.score')}</p>
                <p className="font-medium">{top.score.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
        {answer.windows.map((w) => {
          const scorePercent = Math.round(w.score * 100)
          return (
            <Card key={w.id} className="overflow-hidden border transition-all hover:shadow-md">
              <CardHeader className="bg-muted/30 pb-3 border-b">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex size-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <CalendarDays className="size-4" />
                    </div>
                    <CardTitle className="text-sm font-bold tracking-tight">
                      {w.start} → {w.end}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                      {scorePercent}%
                    </span>
                    <ConfidenceBadge level={w.confidence} />
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pt-4">
                {/* Barra de progreso global del score */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="font-medium text-muted-foreground">{t('when.demandStrength', 'Fuerza de la demanda')}</span>
                    <span className="font-semibold text-foreground">{scorePercent}/100</span>
                  </div>
                  <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                    <div
                      className="bg-gradient-to-r from-teal-500 to-primary h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(5, Math.min(100, scorePercent))}%` }}
                    />
                  </div>
                </div>

                <p className="text-sm font-medium leading-relaxed text-foreground/90">
                  {w.rationale[locale] ?? w.rationale.en}
                </p>

                {/* Desglose visual de factores/indicadores */}
                <div className="rounded-xl border bg-card/60 p-3 space-y-2.5">
                  <div className="flex items-center justify-between text-xs border-b pb-1.5 font-semibold text-muted-foreground">
                    <span>{t('when.indicators', 'Factores analizados')}</span>
                    <span>{t('when.impact', 'Impacto')}</span>
                  </div>

                  <div className="space-y-2">
                    {w.contributions.slice(0, 5).map((c) => {
                      const label = t(`when.indicatorLabels.${c.metric_key}`, c.metric_key)
                      // Contribución relativa normalizada al 100% de la barra
                      const contributionPct = Math.min(100, Math.round((c.contribution / 0.40) * 100))

                      return (
                        <div key={c.metric_key} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-medium text-foreground truncate max-w-[200px]" title={label}>
                              {label}
                            </span>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <DataTag isEstimate={c.is_estimate} datasetIds={c.dataset_ids} />
                              <span className="font-mono text-xs font-semibold text-foreground w-9 text-right">
                                +{c.contribution.toFixed(2)}
                              </span>
                            </div>
                          </div>
                          {/* Mini barra de aportación */}
                          <div className="bg-muted/70 h-1.5 w-full overflow-hidden rounded-full">
                            <div
                              className="bg-primary/70 h-full rounded-full"
                              style={{ width: `${Math.max(4, contributionPct)}%` }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <p className="text-muted-foreground text-[11px] truncate" title={w.dataset_ids.join(', ')}>
                  <span className="font-medium">{t('common.sources')}:</span> {w.dataset_ids.join(', ')}
                </p>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
